import React, { useEffect, useRef, useState } from 'react';
import { VendorTaglineTextField } from '../../../../components/VendorTaglineField';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Switch, FormControlLabel, TextField } from '@material-ui/core';
import { Alert as MuiAlert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fetchRestaurantProfile, updateRestaurantProfile, updateOpenHours } from '../../../../actions/restaurantProfileActions';
import { uploadExactVendorProfileMedia } from '../../../../utilities/imageUpload';
import { apiFetchSections } from '../../../../api';
import VendorProfileMediaDeleteDialog from '../../../../components/VendorProfileMediaDeleteDialog';
import { useVendorMediaCrop } from '../../../../components/VendorMediaCrop';
import ConfirmDialog, { useConfirmDialog } from '../../../../components/ConfirmDialog';
import { getUserFromLocalStorage } from '../../../../utilities/user.js';
import OpenHoursManager from '../../../../components/OpenHours/OpenHoursManager';
import StoreOfferCard from '../../../../components/StoreOfferCard';
import AlertSoundPrefs from '../../../../components/AlertSoundPrefs';
import VendorAlertSettings from '../../../../components/VendorAlertSettings';
import VendorTaxSettings from '../../../../components/VendorTaxSettings';
import { useGate } from '../../../../components/team/Gated';
import {
    AddAPhoto,
    DeleteOutline,
    Panorama,
    Restaurant,
    NotificationsNone,
    Settings,
    Warning,
    CheckCircleOutline,
    InfoOutlined,
    AccessTime,
    PhotoCamera,
    LocalOffer,
    ReceiptOutlined,
} from '@material-ui/icons';

// ─── Logo: 588×558 px  |  Banner: 1440×175 px ────────────────────────────────
const LOGO_W = 588;
const LOGO_H = 558;
const BANNER_W = 1440;
const BANNER_H = 175;
// Card image: the 16:10 frame the mobile Restaurants list and the web grid use.
const CARD_W = 1440;
const CARD_H = 900;

const useStyles = makeStyles(() => ({
    muiLabel: { fontWeight: 'normal', fontSize: '12px', textTransform: 'capitalize' },
    textField: {
        '& .MuiOutlinedInput-root input': { padding: '12px 14px' },
        '& .MuiInputLabel-outlined': { transform: 'translate(14px, 14px) scale(1)' },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
            backgroundColor: '#fff',
            padding: '0 4px',
        },
    },
    // Two columns on desktop, single column on phones so fields aren't cramped.
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        '@media (max-width: 640px)': {
            gridTemplateColumns: '1fr',
            gap: '14px',
        },
    },
}));

/* ─── Inline styles (shared with RetailerWholesalerProfile) ─────────────── */
const S = {
    page: { padding: '0 0 40px', background: '#f8fafc', minHeight: '100vh' },
    bannerWrap: {
        position: 'relative', width: '100%', height: '160px',
        background: 'linear-gradient(135deg, #033b60 0%, #004553 100%)',
        overflow: 'hidden', flexShrink: 0,
    },
    bannerImg: { width: '100%', height: '100%', objectFit: 'cover' },
    bannerOverlay: {
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.22)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
        padding: '12px 16px',
    },
    bannerEditBtn: {
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'rgba(255,255,255,0.18)',
        border: '1.5px solid rgba(255,255,255,0.5)',
        borderRadius: '8px', color: '#fff', fontWeight: 600, fontSize: '0.78rem',
        padding: '6px 12px', cursor: 'pointer', backdropFilter: 'blur(4px)',
    },
    logoWrap: {
        position: 'relative', width: '80px', height: '80px',
        borderRadius: '16px', border: '3px solid #fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)', overflow: 'hidden',
        background: '#e8f4fb', cursor: 'pointer', flexShrink: 0,
    },
    logoImg: { width: '100%', height: '100%', objectFit: 'cover' },
    logoPlaceholder: {
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#033b60',
    },
    logoEditOverlay: {
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0, transition: 'opacity 0.18s',
    },
    profileHeader: {
        padding: '16px 28px 14px',
        display: 'flex', alignItems: 'center', gap: '16px',
        background: '#fff', borderBottom: '1px solid #f1f5f9',
    },
    shopName: { fontWeight: 700, fontSize: '1.2rem', color: '#111827', lineHeight: 1.3 },
    shopStatus: (active) => ({
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        background: active ? '#dcfce7' : '#fee2e2',
        color: active ? '#16a34a' : '#dc2626',
        border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
        borderRadius: '20px', padding: '2px 10px',
        fontSize: '0.72rem', fontWeight: 700, marginTop: '4px',
    }),
    tabBar: {
        display: 'flex', gap: 0,
        borderBottom: '1px solid #e2e8f0',
        padding: '0 28px', background: '#fff', overflowX: 'auto',
    },
    tab: (active) => ({
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '12px 18px',
        fontWeight: active ? 700 : 500, fontSize: '0.85rem',
        color: active ? '#033b60' : '#6b7280',
        // Longhand-only borders: mixing the `border` shorthand with `borderBottom`
        // makes React warn ("Updating borderBottom while border is set") when the
        // active tab changes. Set each side explicitly instead.
        borderTop: 'none', borderLeft: 'none', borderRight: 'none',
        borderBottom: active ? '2px solid #033b60' : '2px solid transparent',
        cursor: 'pointer', background: 'none', outline: 'none',
        transition: 'all 0.15s', whiteSpace: 'nowrap',
    }),
    card: {
        margin: '24px 28px', background: '#fff',
        borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '28px',
    },
    sectionTitle: {
        fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px',
    },
    imgPanel: {
        border: '1.5px dashed #e2e8f0', borderRadius: '12px', background: '#fafafa',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', overflow: 'hidden', position: 'relative',
        cursor: 'pointer',
    },
    imgActions: { display: 'flex', gap: '12px', marginTop: '10px', justifyContent: 'center' },
    imgActionBtn: (color) => ({
        background: 'none', border: 'none', cursor: 'pointer', color,
        fontWeight: 600, fontSize: '0.8rem',
        display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0',
    }),
    reqBox: {
        background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px',
        padding: '12px 14px', fontSize: '0.78rem', color: '#0369a1', lineHeight: 1.6,
    },
    reqRow: { display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' },
    notifRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 0', borderBottom: '1px solid #f1f5f9',
    },
    notifLabel: { fontSize: '0.9rem', fontWeight: 600, color: '#374151' },
    notifHelper: { fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' },
    dangerCard: {
        background: '#fff5f5', border: '1.5px solid #fecaca',
        borderRadius: '12px', padding: '20px', marginBottom: '16px',
    },
    footerBar: {
        position: 'sticky', bottom: 0, background: '#fff',
        borderTop: '1px solid #e2e8f0', padding: '14px 28px',
        display: 'flex', justifyContent: 'flex-end', gap: '10px', zIndex: 10,
    },
    btnPrimary: {
        padding: '10px 28px', borderRadius: '10px', border: 'none',
        background: 'linear-gradient(135deg, #033b60 0%, #004553 100%)',
        color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(3,59,96,0.28)',
        display: 'flex', alignItems: 'center', gap: '8px', transition: 'opacity 0.2s',
    },
    btnSecondary: {
        padding: '10px 20px', borderRadius: '10px',
        border: '1.5px solid #e2e8f0', background: '#fff',
        color: '#6b7280', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
    },
};

const TABS = [
    { id: 'account',       label: 'Account',        Icon: Restaurant },
    { id: 'media',         label: 'Logo & Banner',   Icon: PhotoCamera },
    { id: 'hours',         label: 'Business Hours',  Icon: AccessTime },
    { id: 'offers',        label: 'Offers',          Icon: LocalOffer },
    { id: 'notifications', label: 'Notifications',   Icon: NotificationsNone },
    /* Next to Notifications rather than buried under Advanced: URA receipting
       is something a vendor is asked to make a decision about, not a setting
       they tune once. Advanced is where things go to be forgotten. */
    { id: 'tax',           label: 'Tax & Receipts',  Icon: ReceiptOutlined },
    { id: 'advanced',      label: 'Advanced',        Icon: Settings },
    { id: 'deactivate',    label: 'Deactivate',      Icon: Warning },
];

// Email, SMS and WhatsApp are owned by the shared Alert settings panel
// (VendorAlertSettings). The server keeps the legacy fields; only the two
// channels that panel does not cover are still toggled here.
const NOTIF_OPTIONS = [
    { key: 'phoneCall', label: 'Phone Call',  helper: 'Get called for new or urgent orders' },
    { key: 'dropOff',   label: 'Drop-Off',    helper: 'Notifications for drop-off type orders' },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const RestaurantProfile = () => {
    const user = getUserFromLocalStorage();
    const classes = useStyles();
    const { fitVendorMedia, mediaCropDialog } = useVendorMediaCrop();
    const dispatch = useDispatch();
    const { profile, loading, error } = useSelector((state) => state.restaurantProfile);

    const vendorId = user?.result?._id;
    // Store-wide offer: the menu sections feed the "selected sections" scope.
    const [offerSections, setOfferSections] = useState([]);
    const [savingOffer, setSavingOffer] = useState(false);
    useEffect(() => {
        let alive = true;
        if (!vendorId) return undefined;
        apiFetchSections(vendorId)
            .then((res) => { if (alive) setOfferSections(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (alive) setOfferSections([]); });
        return () => { alive = false; };
    }, [vendorId]);
    const handleStoreOfferSave = async (storeOffer) => {
        setSavingOffer(true);
        try {
            await dispatch(updateRestaurantProfile(vendorId, { storeOffer }));
            toast.success(storeOffer.enabled ? 'Store-wide offer saved.' : 'Store-wide offer ended.');
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to save the offer.');
        } finally {
            setSavingOffer(false);
        }
    };
    // Only a Lusuku super admin may edit the phone number; vendors see it locked.
    const isSuperAdmin =
        Boolean(user?.result?.isSuperAdmin) ||
        user?.result?.userType === 'LUSUKU STAFF' ||
        ['SUPERADMIN', 'SUPERUSER'].includes(String(user?.result?.user_role || user?.result?.role || '').toUpperCase());

    const logoInputRef   = useRef(null);
    const bannerInputRef = useRef(null);

    const [tab,   setTab]   = useState('account');
    const [saving, setSaving] = useState(false);
    // Vendor team permissions: saving the vendor profile is settings:edit.
    const settingsGate = useGate('settings:edit');

    const [form, setForm] = useState({
        name: '', address: '', phone: '', email: '',
        latitude: '', longitude: '', deliveryZoneRadius: 5,
        isActive: false, imageUrl: '', bannerUrl: '', shopBannerUrl: '', cardImageUrl: '', tagline: '',
        defaultPrepMinutes: 20, maxPrepMinutes: 90,
    });
    const [notifications, setNotifications] = useState({
        sms: false, email: false, whatsapp: false, phoneCall: false, dropOff: false,
    });

    // Baseline snapshots for dirty-state detection
    const [savedForm, setSavedForm] = useState(null);
    const [savedNotifications, setSavedNotifications] = useState(null);

    const [logoFile,       setLogoFile]       = useState(null);
    const [logoPreview,    setLogoPreview]    = useState(null);
    const [logoHover,      setLogoHover]      = useState(false);
    const [logoUploading,  setLogoUploading]  = useState(false);

    const [bannerFile,      setBannerFile]      = useState(null);
    const [bannerPreview,   setBannerPreview]   = useState(null);
    const [bannerUploading, setBannerUploading] = useState(false);
    const [cardFile,        setCardFile]        = useState(null);
    // The saved image whose Delete button was clicked; opens the delete dialog.
    const [mediaDeleteRole, setMediaDeleteRole] = useState(null);
    const [cardPreview,     setCardPreview]     = useState(null);
    const [cardUploading,   setCardUploading]   = useState(false);
    const cardInputRef = useRef(null);

    const [deactivationConfirm, setDeactivationConfirm] = useState('');
    const [isDeactivating,      setIsDeactivating]      = useState(false);

    useEffect(() => { dispatch(fetchRestaurantProfile(vendorId)); }, [dispatch, vendorId]);

    useEffect(() => {
        if (profile) {
            const nextForm = {
                name: '', address: '', phone: '', email: '',
                latitude: '', longitude: '', deliveryZoneRadius: 5,
                isActive: false, imageUrl: '', bannerUrl: '', shopBannerUrl: '', cardImageUrl: '', tagline: '',
                defaultPrepMinutes: 20, maxPrepMinutes: 90,
                ...profile,
            };
            const nextNotifs = {
                sms: false, email: false, whatsapp: false, phoneCall: false, dropOff: false,
                ...(profile.notifications || {}),
            };
            setForm(nextForm);
            setNotifications(nextNotifs);
            setSavedForm(nextForm);
            setSavedNotifications(nextNotifs);
        }
    }, [profile]);

    // True when any field or staged image differs from the last-saved snapshot
    const isDirty = (() => {
        if (!savedForm) return false;
        if (logoFile || bannerFile || cardFile) return true;
        if (JSON.stringify(form) !== JSON.stringify(savedForm)) return true;
        if (JSON.stringify(notifications) !== JSON.stringify(savedNotifications)) return true;
        return false;
    })();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSwitch = (e) => {
        const { name, checked } = e.target;
        setForm((p) => ({ ...p, [name]: checked }));
    };

    const handleNotifChange = (e) => {
        const { name, checked } = e.target;
        setNotifications((p) => ({ ...p, [name]: checked }));
    };

    /* ── logo ── */
    const handleLogoChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'restaurant', 'logo');
            if (!file) return; // crop cancelled
            const preview = await new Promise((res) => {
                const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
            });
            setLogoFile(file); setLogoPreview(preview);
            toast.success(`Logo ready (${LOGO_W}×${LOGO_H}px)`);
        } catch (error) { toast.error(error.message || 'Failed to validate logo.'); }
        finally { setLogoUploading(false); e.target.value = ''; }
    };

    /* ── banner ── */
    const handleBannerChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;
        setBannerUploading(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'restaurant', 'shopBanner');
            if (!file) return; // crop cancelled
            const preview = await new Promise((res) => {
                const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
            });
            setBannerFile(file); setBannerPreview(preview);
            toast.success(`Banner ready (${BANNER_W}×${BANNER_H}px)`);
        } catch (error) { toast.error(error.message || 'Failed to validate banner.'); }
        finally { setBannerUploading(false); e.target.value = ''; }
    };

    /* ── card image (what customers see in the restaurant list) ── */
    const handleCardChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;
        setCardUploading(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'restaurant', 'cardImage');
            if (!file) return; // crop cancelled
            const preview = await new Promise((res) => {
                const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
            });
            setCardFile(file); setCardPreview(preview);
            toast.success(`Card image ready (${CARD_W}×${CARD_H}px)`);
        } catch (error) { toast.error(error.message || 'Failed to validate card image.'); }
        finally { setCardUploading(false); e.target.value = ''; }
    };

    /* ── submit ── */
    const handleSubmit = async () => {
        setSaving(true);
        let updated = { ...form };
        try {
            if (logoFile) {
                const r = await uploadExactVendorProfileMedia(logoFile, {
                    vendorType: "restaurant",
                    vendorId,
                    mediaRole: "logo",
                    entityType: "restaurantProfile",
                    entityId: vendorId,
                    source: "dashboard",
                });
                updated.imageUrl = r.secure_url;
                setLogoFile(null); setLogoPreview(null);
            }
            if (bannerFile) {
                const r = await uploadExactVendorProfileMedia(bannerFile, {
                    vendorType: "restaurant",
                    vendorId,
                    mediaRole: "shopBanner",
                    entityType: "restaurantProfile",
                    entityId: vendorId,
                    source: "dashboard",
                });
                updated.shopBannerUrl = r.secure_url;
                updated.bannerUrl = '';
                setBannerFile(null); setBannerPreview(null);
            }
            if (cardFile) {
                const r = await uploadExactVendorProfileMedia(cardFile, {
                    vendorType: "restaurant",
                    vendorId,
                    mediaRole: "cardImage",
                    entityType: "restaurantProfile",
                    entityId: vendorId,
                    source: "dashboard",
                });
                updated.cardImageUrl = r.secure_url;
                setCardFile(null); setCardPreview(null);
            }
            await dispatch(updateRestaurantProfile(vendorId, { ...updated, notifications }));
            setForm(updated);
            setSavedForm(updated);
            setSavedNotifications({ ...notifications });
            toast.success('Profile updated successfully.');
        } catch (err) {
            toast.error(err?.message || 'Failed to update profile.');
        } finally { setSaving(false); }
    };

    // An unsaved pick is only a local preview: discard it with no server call
    // and no confirmation. A saved image opens VendorProfileMediaDeleteDialog,
    // which shows every saved image and lets the vendor tick more than one.
    const handleRemoveMedia = (mediaRole) => {
        const isLogo = mediaRole === 'logo';
        const isCard = mediaRole === 'cardImage';
        const stagedFile = isLogo ? logoFile : isCard ? cardFile : bannerFile;
        if (stagedFile) {
            if (isLogo) { setLogoFile(null); setLogoPreview(null); }
            else if (isCard) { setCardFile(null); setCardPreview(null); }
            else { setBannerFile(null); setBannerPreview(null); }
            return;
        }
        setMediaDeleteRole(mediaRole);
    };

    /* ── open hours ── */
    const handleOpenHoursSave = async (openHours, timezone) => {
        try {
            await dispatch(updateOpenHours(vendorId, openHours, timezone));
            toast.success('Business hours updated.');
        } catch { toast.error('Failed to update hours.'); }
    };

    /* ── deactivate / reactivate ── */
    const confirmDialog = useConfirmDialog();

    const handleDeactivate = () => {
        if (deactivationConfirm !== profile.name) {
            toast.error('Restaurant name does not match. Type it exactly.'); return;
        }
        confirmDialog.ask({
            title: 'Hide your restaurant from customers?',
            body: 'Your restaurant and its menu disappear from the apps and lusuku.ug until you reactivate it. Nothing is deleted.',
            tone: 'warning',
            confirmLabel: 'Deactivate',
            busyLabel: 'Deactivating...',
            onConfirm: async () => {
                setIsDeactivating(true);
                try {
                    await dispatch(updateRestaurantProfile(vendorId, { isActive: false }));
                    dispatch(fetchRestaurantProfile(vendorId));
                    toast.success('Restaurant deactivated.');
                } catch { toast.error('Failed to deactivate.'); }
                finally { setIsDeactivating(false); setDeactivationConfirm(''); }
            },
        });
    };

    const handleReactivate = () => {
        confirmDialog.ask({
            title: 'Reactivate your restaurant?',
            body: 'It becomes visible to customers again straight away.',
            tone: 'success',
            confirmLabel: 'Reactivate',
            busyLabel: 'Reactivating...',
            onConfirm: async () => {
                setIsDeactivating(true);
                try {
                    await dispatch(updateRestaurantProfile(vendorId, { isActive: true }));
                    dispatch(fetchRestaurantProfile(vendorId));
                    toast.success('Restaurant reactivated.');
                } catch { toast.error('Failed to reactivate.'); }
                finally { setIsDeactivating(false); }
            },
        });
    };

    if (loading && !profile) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress style={{ color: '#033b60' }} />
            </div>
        );
    }

    const currentLogo   = logoPreview   || form.imageUrl  || null;
    const currentBanner = bannerPreview || form.shopBannerUrl || form.bannerUrl || null;
    const currentCard = cardPreview || form.cardImageUrl || null;

    return (
        <div style={S.page}>

            {/* Reactivation alert */}
            {profile && !profile.isActive && (
                <MuiAlert
                    severity="warning"
                    style={{ margin: '0 28px 16px', borderRadius: '10px' }}
                    action={
                        <button
                            style={{ ...S.btnPrimary, padding: '6px 16px', fontSize: '0.8rem' }}
                            onClick={handleReactivate}
                            disabled={isDeactivating}
                        >
                            Reactivate Now
                        </button>
                    }
                >
                    <AlertTitle>Restaurant Deactivated</AlertTitle>
                    Your restaurant is hidden from customers and not accepting new orders.
                </MuiAlert>
            )}

            {error && (
                <div style={{ margin: '0 28px 16px', padding: '12px 16px', background: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontSize: '0.875rem' }}>
                    Error: {error}
                </div>
            )}

            {/* Banner */}
            <div style={S.bannerWrap}>
                {currentBanner && <img src={currentBanner} alt="Restaurant banner" style={S.bannerImg} />}
                <div style={S.bannerOverlay}>
                    <button style={S.bannerEditBtn} onClick={() => bannerInputRef.current?.click()}>
                        <Panorama style={{ fontSize: '1rem' }} />
                        {currentBanner ? 'Change Banner' : 'Add Banner'}
                    </button>
                    <input ref={bannerInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleBannerChange} />
                </div>
                {bannerUploading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
                        <Spinner animation="border" style={{ color: '#fff' }} />
                    </div>
                )}
            </div>

            {/* Profile header — below banner, no overlap */}
            <div style={S.profileHeader}>
                <div
                    style={S.logoWrap}
                    onMouseEnter={() => setLogoHover(true)}
                    onMouseLeave={() => setLogoHover(false)}
                    onClick={() => logoInputRef.current?.click()}
                    title="Click to change logo"
                >
                    {logoUploading ? (
                        <div style={S.logoPlaceholder}><Spinner animation="border" size="sm" /></div>
                    ) : currentLogo ? (
                        <img src={currentLogo} alt="Restaurant logo" style={S.logoImg} />
                    ) : (
                        <div style={S.logoPlaceholder}>
                            <Restaurant style={{ fontSize: '2rem' }} />
                        </div>
                    )}
                    <div style={{ ...S.logoEditOverlay, opacity: logoHover ? 1 : 0 }}>
                        <AddAPhoto style={{ color: '#fff', fontSize: '1.2rem' }} />
                    </div>
                    <input ref={logoInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleLogoChange} />
                </div>

                <div>
                    <div style={S.shopName}>{form.name || 'Your Restaurant'}</div>
                    <div style={S.shopStatus(form.isActive)}>
                        <CheckCircleOutline style={{ fontSize: '0.82rem' }} />
                        {form.isActive ? 'Active' : 'Inactive'}
                    </div>
                </div>
            </div>

            {/* Tab bar */}
            <div style={S.tabBar}>
                {TABS.map(({ id, label, Icon }) => (
                    <button key={id} style={S.tab(tab === id)} onClick={() => setTab(id)}>
                        <Icon style={{ fontSize: '1rem' }} />
                        {label}
                    </button>
                ))}
            </div>

            {/* ── Account ── */}
            {tab === 'account' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Restaurant Details</div>
                    <div className={classes.formGrid}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <TextField
                                fullWidth label="Restaurant Name" name="name"
                                value={form.name || ''} onChange={handleChange}
                                disabled variant="outlined" size="small"
                                helperText="Contact support to change your registered name."
                                className={classes.textField} InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <VendorTaglineTextField
                                value={form.tagline}
                                onChange={handleChange}
                                className={classes.textField}
                            />
                        </div>
                        <TextField
                            fullWidth label="Phone Number" name="phone" disabled={!isSuperAdmin}
                            value={form.phone || ''} onChange={handleChange}
                            variant="outlined" size="small" placeholder="+256..."
                            helperText={!isSuperAdmin ? 'Only a Lusuku admin can change this — contact support.' : undefined}
                            className={classes.textField} InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth label="Email Address" name="email" type="email"
                            value={form.email || ''} onChange={handleChange}
                            variant="outlined" size="small" placeholder="restaurant@email.com"
                            className={classes.textField} InputLabelProps={{ shrink: true }}
                        />
                        <div style={{ gridColumn: '1 / -1' }}>
                            <TextField
                                fullWidth label="Address" name="address"
                                value={form.address || ''} onChange={handleChange}
                                variant="outlined" size="small" multiline minRows={2}
                                placeholder="e.g. Plot 12, Kampala Road, Kampala"
                                className={classes.textField} InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '4px' }}>
                            Location Coordinates
                        </div>
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.7rem', color: '#9ca3af', marginTop: '-2px' }}>
                            Locked to prevent accidental changes — contact support to update your coordinates.
                        </div>
                        <TextField
                            fullWidth label="Latitude" name="latitude" type="number" disabled
                            value={form.latitude ?? ''} onChange={handleChange}
                            variant="outlined" size="small" inputProps={{ step: 'any' }}
                            placeholder="e.g. 0.3476"
                            className={classes.textField} InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth label="Longitude" name="longitude" type="number" disabled
                            value={form.longitude ?? ''} onChange={handleChange}
                            variant="outlined" size="small" inputProps={{ step: 'any' }}
                            placeholder="e.g. 32.5825"
                            className={classes.textField} InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth label="Delivery Zone Radius (km)" name="deliveryZoneRadius" type="number"
                            value={form.deliveryZoneRadius ?? 5} onChange={handleChange}
                            variant="outlined" size="small" inputProps={{ min: 1 }}
                            className={classes.textField} InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </div>
            )}

            {/* ── Logo & Banner ── */}
            {tab === 'media' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Restaurant Logo</div>
                    <div className={classes.formGrid} style={{ marginBottom: '32px' }}>
                        <div>
                            <div style={{ ...S.imgPanel, height: '220px' }} onClick={() => logoInputRef.current?.click()}>
                                {logoUploading ? <Spinner animation="border" /> :
                                    currentLogo ? (
                                        <img src={currentLogo} alt="Logo" style={{ maxHeight: '190px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#9ca3af' }}>
                                            <AddAPhoto style={{ fontSize: '2.5rem' }} />
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Click to upload logo</span>
                                        </div>
                                    )
                                }
                            </div>
                            <div style={S.imgActions}>
                                <button style={S.imgActionBtn('#033b60')} onClick={() => logoInputRef.current?.click()}>
                                    <AddAPhoto style={{ fontSize: '0.9rem' }} /> {currentLogo ? 'Replace' : 'Upload'}
                                </button>
                                {currentLogo && (
                                    <button style={S.imgActionBtn('#dc2626')} onClick={() => handleRemoveMedia('logo')}>
                                        <DeleteOutline style={{ fontSize: '0.9rem' }} /> Remove
                                    </button>
                                )}
                            </div>
                            <input ref={logoInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleLogoChange} />
                        </div>
                        <div style={S.reqBox}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1' }}>Logo requirements</div>
                            {[
                                `JPG, PNG, or WebP, max 2 MB`,
                                `Exact size: ${LOGO_W} × ${LOGO_H} px. Any other size opens a crop step.`,
                                `Shown as a square tile in the mobile app and web storefront`,
                                `Use a white or plain background for best results`,
                            ].map((t) => (
                                <div key={t} style={S.reqRow}>
                                    <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={S.sectionTitle}>Restaurant Banner</div>
                    <div className={classes.formGrid}>
                        <div>
                            <div style={{ ...S.imgPanel, height: '110px' }} onClick={() => bannerInputRef.current?.click()}>
                                {bannerUploading ? <Spinner animation="border" /> :
                                    currentBanner ? (
                                        <img src={currentBanner} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#9ca3af' }}>
                                            <Panorama style={{ fontSize: '2.5rem' }} />
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Click to upload banner</span>
                                        </div>
                                    )
                                }
                            </div>
                            <div style={S.imgActions}>
                                <button style={S.imgActionBtn('#033b60')} onClick={() => bannerInputRef.current?.click()}>
                                    <AddAPhoto style={{ fontSize: '0.9rem' }} /> {currentBanner ? 'Replace' : 'Upload'}
                                </button>
                                {currentBanner && (
                                    <button style={S.imgActionBtn('#dc2626')} onClick={() => handleRemoveMedia('shopBanner')}>
                                        <DeleteOutline style={{ fontSize: '0.9rem' }} /> Remove
                                    </button>
                                )}
                            </div>
                            <input ref={bannerInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleBannerChange} />
                        </div>
                        <div style={S.reqBox}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1' }}>Banner requirements</div>
                            {[
                                `JPG, PNG, or WebP, max 5 MB`,
                                `Exact size: ${BANNER_W} × ${BANNER_H} px. Any other size opens a crop step.`,
                                `Full-width hero strip on your restaurant page`,
                                `Keep key content away from edges — may be cropped on small screens`,
                            ].map((t) => (
                                <div key={t} style={S.reqRow}>
                                    <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={S.sectionTitle}>Card Image</div>
                    <div className={classes.formGrid}>
                        <div>
                            <div style={{ ...S.imgPanel, aspectRatio: '16 / 10', height: 'auto' }} onClick={() => cardInputRef.current?.click()}>
                                {cardUploading ? <Spinner animation="border" /> :
                                    currentCard ? (
                                        <img src={currentCard} alt="Card" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#9ca3af' }}>
                                            <Panorama style={{ fontSize: '2.5rem' }} />
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Click to upload card image</span>
                                            <span style={{ fontSize: '0.75rem' }}>Optional. Without it, your pinned or first dish photo is used.</span>
                                        </div>
                                    )
                                }
                            </div>
                            <div style={S.imgActions}>
                                <button style={S.imgActionBtn('#033b60')} onClick={() => cardInputRef.current?.click()}>
                                    <AddAPhoto style={{ fontSize: '0.9rem' }} /> {currentCard ? 'Replace' : 'Upload'}
                                </button>
                                {currentCard && (
                                    <button style={S.imgActionBtn('#dc2626')} onClick={() => handleRemoveMedia('cardImage')}>
                                        <DeleteOutline style={{ fontSize: '0.9rem' }} /> Remove
                                    </button>
                                )}
                            </div>
                            <input ref={cardInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleCardChange} />
                        </div>
                        <div style={S.reqBox}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1' }}>Card image requirements</div>
                            {[
                                `JPG, PNG, or WebP, max 5 MB`,
                                `Exact size: ${CARD_W} × ${CARD_H} px (16:10). Any other size opens a crop step.`,
                                `This is the picture customers see in the restaurant list on the app and the website`,
                                `It replaces the dish photo on your card; remove it to go back to your pinned dish`,
                                `A photo of food sells better than a logo; keep text off the picture`,
                            ].map((t) => (
                                <div key={t} style={S.reqRow}>
                                    <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Business Hours ── */}
            {tab === 'hours' && (
                <div style={S.card}>
                    <OpenHoursManager
                        vendorType="restaurant"
                        vendorId={vendorId}
                        openHours={profile?.openHours}
                        timezone={profile?.timezone || 'Africa/Kampala'}
                        onSave={handleOpenHoursSave}
                        onValidationError={(msg) => toast.error(msg)}
                        isLoading={loading}
                    />
                </div>
            )}

            {/* ── Notifications ── */}
            {/* ── Store-wide offer (vendor discounts) ── */}
            {tab === 'offers' && (
                <div style={S.card}>
                    <StoreOfferCard
                        value={profile?.storeOffer}
                        sections={offerSections}
                        saving={savingOffer}
                        onSave={handleStoreOfferSave}
                    />
                </div>
            )}

            {tab === 'tax' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Tax and receipts</div>
                    {/* Shared VendorTaxSettings panel, backed by /api/vendor-tax.
                        Self-scoped: it resolves this account's own shop server-side,
                        so it takes no props and cannot be pointed at anyone else. */}
                    <VendorTaxSettings />
                </div>
            )}

            {tab === 'notifications' && (
                <div style={S.card}>
                    <VendorAlertSettings />
                    <div style={{ borderTop: '1px solid #eef1f6', margin: '18px 0' }} />
                    <div style={S.sectionTitle}>Voice and drop-off alerts</div>
                    {NOTIF_OPTIONS.map(({ key, label, helper }) => (
                        <div key={key} style={S.notifRow}>
                            <div>
                                <div style={S.notifLabel}>{label}</div>
                                <div style={S.notifHelper}>{helper}</div>
                            </div>
                            <Switch
                                checked={!!notifications[key]}
                                onChange={handleNotifChange}
                                name={key} color="primary" size="small"
                            />
                        </div>
                    ))}
                    <div style={{ borderTop: '1px solid #eef1f6', margin: '18px 0 0', paddingTop: 18 }}>
                        <AlertSoundPrefs />
                    </div>
                </div>
            )}

            {/* ── Advanced ── */}
            {tab === 'advanced' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Restaurant Visibility</div>
                    <FormControlLabel
                        control={<Switch checked={!!form.isActive} onChange={handleSwitch} name="isActive" color="primary" />}
                        label={form.isActive ? 'Restaurant is Active' : 'Restaurant is Deactivated'}
                        classes={{ label: classes.muiLabel }}
                    />
                    <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '8px' }}>
                        When deactivated, your restaurant is hidden from all customers and stops accepting new orders.
                    </p>

                    <div style={{ ...S.sectionTitle, marginTop: '28px' }}>Order Prep Time</div>
                    <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '16px', lineHeight: 1.5 }}>
                        The <strong>default prep time</strong> is pre-filled when the cashier accepts an order.
                        The <strong>maximum prep time</strong> is the ceiling — any prep time that exceeds it is
                        automatically capped and flagged as delayed on the terminal.
                    </p>
                    <div className={classes.formGrid}>
                        <TextField
                            fullWidth
                            label="Default Prep Time (mins)"
                            name="defaultPrepMinutes"
                            type="number"
                            value={form.defaultPrepMinutes ?? 20}
                            onChange={handleChange}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1, max: 240 }}
                            helperText="Typical time to prepare one order (1–240 min)"
                            className={classes.textField}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Maximum Prep Time (mins)"
                            name="maxPrepMinutes"
                            type="number"
                            value={form.maxPrepMinutes ?? 90}
                            onChange={handleChange}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1, max: 480 }}
                            helperText="Orders capped here are flagged as delayed (1–480 min)"
                            className={classes.textField}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Business day starts at"
                            name="dayStartsAt"
                            type="time"
                            value={form.dayStartsAt || "00:00"}
                            onChange={handleChange}
                            variant="outlined"
                            size="small"
                            inputProps={{ step: 300 }}
                            helperText="Daily plate limits count from this time to the next. A kitchen serving past midnight can set 05:00 so one service stays one day."
                            className={classes.textField}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </div>
            )}

            {/* ── Deactivate ── */}
            {tab === 'deactivate' && (
                <div style={S.card}>
                    {profile && profile.isActive ? (
                        <>
                            <div style={S.dangerCard}>
                                <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '8px', fontSize: '0.95rem' }}>
                                    Deactivating will hide your restaurant
                                </div>
                                <ul style={{ fontSize: '0.85rem', color: '#374151', paddingLeft: '18px', margin: 0 }}>
                                    <li>Hidden from all customer searches</li>
                                    <li>No new orders will be accepted</li>
                                    <li>Existing orders must still be fulfilled</li>
                                    <li>All menu data is preserved — reactivate any time</li>
                                </ul>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '10px' }}>
                                Type your restaurant name exactly to confirm: <strong>{profile.name}</strong>
                            </p>
                            <TextField
                                fullWidth variant="outlined" size="small"
                                placeholder={`Type "${profile.name}" to confirm`}
                                value={deactivationConfirm}
                                onChange={(e) => setDeactivationConfirm(e.target.value)}
                                style={{ marginBottom: '14px' }}
                            />
                            <button
                                style={{
                                    ...S.btnPrimary,
                                    background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
                                    boxShadow: '0 4px 12px rgba(185,28,28,0.3)',
                                    opacity: (deactivationConfirm !== profile?.name || isDeactivating) ? 0.5 : 1,
                                }}
                                disabled={deactivationConfirm !== profile?.name || isDeactivating}
                                onClick={handleDeactivate}
                            >
                                {isDeactivating ? <Spinner animation="border" size="sm" /> : 'Deactivate Restaurant'}
                            </button>
                        </>
                    ) : (
                        <MuiAlert severity="info" style={{ borderRadius: '10px' }}>
                            <AlertTitle>Restaurant Already Deactivated</AlertTitle>
                            Use the "Reactivate Now" button at the top of the page to restore visibility.
                        </MuiAlert>
                    )}
                </div>
            )}

            {/* Sticky footer */}
            <div style={S.footerBar}>
                <button
                    style={{ ...S.btnSecondary, opacity: isDirty ? 1 : 0.4, cursor: isDirty ? 'pointer' : 'default' }}
                    onClick={() => isDirty && dispatch(fetchRestaurantProfile(vendorId))}
                    disabled={!isDirty}
                >
                    Discard Changes
                </button>
                <button
                    style={{ ...S.btnPrimary, opacity: (saving || !isDirty) ? 0.4 : 1, cursor: (saving || !isDirty) ? 'default' : 'pointer' }}
                    onClick={handleSubmit}
                    disabled={(saving || !isDirty) || !settingsGate.allowed}
                    title={settingsGate.title}
                >
                    {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                </button>
            </div>

            <ConfirmDialog {...confirmDialog.props} />
            {mediaCropDialog}
            <VendorProfileMediaDeleteDialog
                role={mediaDeleteRole}
                vendorType="restaurant"
                vendorId={vendorId}
                vendorName={profile?.name}
                media={form}
                owner="your restaurant"
                onClose={() => setMediaDeleteRole(null)}
                onRemoved={(cleared) => {
                    // The server has already cleared these, so the baseline
                    // moves too: deleting an image must not mark the form dirty.
                    setForm((p) => ({ ...p, ...cleared }));
                    setSavedForm((p) => (p ? { ...p, ...cleared } : p));
                }}
            />
        </div>
    );
};

export default RestaurantProfile;
